import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useState, useEffect, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/index";
import { Loading } from "../components/Loading";
import { getPublicJobOfferById, uploadCvForPublicJob } from "../services/job.service";
import { useJobDownload } from "../hooks/useJobDownload";
import { TENANT_LOCALE_STORAGE_KEY } from "../i18n/config";
import { normalizeLocale } from "@/utils/locale";
import { JobOffer } from "../models/job";
import { Button } from '@/components/ui/atoms/Button';
import { AlertTriangle, Briefcase, MapPin, GraduationCap, Clock, CheckCircle, Upload, FileText, X, Download } from "lucide-react";

const WHATSAPP_CHAT_URL = "https://wa.me/message/EQXREBSFNFKKO1";
const SUPPORT_EMAIL = "support@invirtus.co";

// Inline WhatsApp glyph — kept local so we don't pull a brand SVG dependency
// just for this footer link.
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M12.04 2.003c-5.523 0-9.998 4.475-9.998 9.998 0 1.762.46 3.486 1.336 5.004L2 22l5.137-1.346a9.99 9.99 0 0 0 4.9 1.247h.005c5.522 0 9.997-4.475 9.997-9.998 0-2.671-1.04-5.182-2.93-7.07a9.927 9.927 0 0 0-7.07-2.93Zm0 18.197h-.004a8.2 8.2 0 0 1-4.18-1.146l-.3-.178-3.05.799.815-2.97-.195-.305a8.2 8.2 0 0 1-1.26-4.4c0-4.527 3.687-8.214 8.218-8.214 2.193 0 4.255.854 5.806 2.408a8.176 8.176 0 0 1 2.404 5.81c-.001 4.529-3.687 8.196-8.254 8.196Zm4.503-6.149c-.247-.123-1.462-.722-1.689-.804-.226-.082-.39-.123-.555.123-.165.247-.637.804-.78.969-.144.165-.288.185-.535.062-.247-.124-1.042-.385-1.984-1.227-.733-.654-1.228-1.46-1.372-1.706-.143-.247-.015-.38.109-.503.112-.111.247-.288.37-.432.124-.144.165-.247.247-.412.082-.165.041-.31-.021-.432-.062-.124-.555-1.338-.761-1.832-.2-.481-.404-.416-.555-.424l-.473-.008c-.165 0-.432.062-.659.31-.226.247-.864.844-.864 2.057s.885 2.388 1.008 2.553c.124.165 1.74 2.658 4.219 3.726.59.255 1.049.408 1.408.521.591.188 1.13.162 1.555.099.474-.071 1.462-.598 1.668-1.175.206-.578.206-1.073.144-1.175-.062-.103-.226-.165-.473-.288Z" />
  </svg>
);

export const PublicJobCvUploadPage = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('cv_upload');
  // Sourcing-channel tag carried by the shared link, e.g. ?source=linkedin
  const source = searchParams.get("source");
  const { downloadJobOfferForCandidate } = useJobDownload();

  const [job, setJob] = useState<JobOffer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  // Default opt-in: by submitting, the candidate agrees to be contacted by
  // the Invirtus team. They can untick this or opt out later by email.
  const [hasOptedIn, setHasOptedIn] = useState(true);

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getPublicJobOfferById(jobId);
        if (cancelled) return;
        if (response.data) {
          if (cancelled) return;
          setJob(response.data);
        } else {
          const message =
            response.error || i18n.t('errors.job_not_found', { ns: 'cv_upload' });
          setError(message);
          toast.error(message);
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : i18n.t('errors.fetch_failed', { ns: 'cv_upload' });
        setError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    const rawLocale = job?.locale;
    if (!rawLocale) return;
    void i18n.changeLanguage(normalizeLocale(rawLocale));
    return () => {
      const tenantLocale =
        localStorage.getItem(TENANT_LOCALE_STORAGE_KEY) || 'en';
      void i18n.changeLanguage(tenantLocale);
    };
  }, [job?.locale]);

  const handleFileSelected = (file: File) => {
    setCvFile(file);
    setUploadSuccess(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!jobId || !cvFile) {
      toast.error(t('toast.select_cv'));
      return;
    }
    if (!hasOptedIn) {
      toast.error(t('toast.optin_required'));
      return;
    }

    setIsUploading(true);
    const loadingToast = toast.loading(t('toast.uploading'));

    try {
      const response = await uploadCvForPublicJob(jobId, cvFile, source);
      if (response.data) {
        toast.success(response.data.message || t('upload.success'));
        setCvFile(null);
        setUploadSuccess(true);
      } else {
        throw new Error(response.error || t('toast.upload_failed'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('toast.upload_error');
      toast.error(message);
    } finally {
      setIsUploading(false);
      toast.dismiss(loadingToast);
    }
  };

  const formatEnumString = (str: string | undefined): string => {
    if (!str) return t('common.na');
    return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return `0 ${t('file_size.bytes')}`;
    const k = 1024;
    const sizes = [t('file_size.bytes'), t('file_size.kb'), t('file_size.mb')];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const renderBadgeList = (items: (string | { name: string; importance: string })[] | undefined) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {items.map((item, index) => {
          const name = typeof item === 'string' ? item : item.name;
          return (
            <span
              key={index}
              className="px-3 py-1 border rounded-full text-xs font-bold tracking-wide bg-white text-dark-invirtus border-dark-invirtus"
            >
              {name}
            </span>
          );
        })}
      </div>
    );
  };

  const skillStack =
    job?.skills && job.skills.length > 0
      ? job.skills
      : [...(job?.codingLanguages || []), ...(job?.frameworksLibraries || [])];
  const brandLogoSrc = job?.workspaceLogoUrl || "/logo/dark-logo.png";
  const brandLogoAlt = job?.companyName || "Invirtus";
  const isValidHexColor = (value?: string | null): value is string =>
    Boolean(value && /^#[0-9A-Fa-f]{6}$/.test(value));
  const brandPrimaryColor = job?.workspacePrimaryColor;
  const brandSecondaryColor = job?.workspaceSecondaryColor;
  const hasBrandedButtonColors =
    isValidHexColor(brandPrimaryColor) && isValidHexColor(brandSecondaryColor);
  const brandedButtonStyle: CSSProperties | undefined = hasBrandedButtonColors
    ? {
        backgroundColor: brandPrimaryColor,
        borderColor: brandPrimaryColor,
        color: brandSecondaryColor,
      }
    : undefined;
  const isSubmitDisabled = isUploading || !cvFile || !hasOptedIn;

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex flex-col items-center justify-center p-4 font-body">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5 border border-red-100">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-black text-dark-invirtus mb-3 font-heading tracking-tight">{t('errors.loading_title')}</h1>
          <p className="text-dark-invirtus/70 text-lg leading-relaxed mb-6">{error}</p>
          <Button
            onClick={() => navigate('/')}
            label={t('errors.return_home')}
            type="contained"
            intent="secondary"
          />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex flex-col items-center justify-center p-4 font-body">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-5 border border-gray-200">
            <Briefcase className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-2xl font-black text-dark-invirtus mb-3 font-heading tracking-tight">{t('errors.not_found_title')}</h1>
          <p className="text-dark-invirtus/70 text-lg leading-relaxed mb-6">
            {t('errors.not_found_message')}
          </p>
          <Button
            onClick={() => navigate('/')}
            label={t('errors.return_home')}
            type="contained"
            intent="secondary"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8] font-body pb-24">
      {/* Hero Banner */}
      <div className="w-full bg-gradient-1 bg-dotted relative overflow-hidden border-b border-gray-200/50 shadow-sm pt-12 pb-16 px-6 md:px-12">
        <div className="absolute top-0 left-10 md:left-24 w-64 h-64 bg-white/40 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 right-10 md:right-24 w-80 h-80 bg-purple/30 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center text-center">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={brandLogoSrc}
              alt={`${brandLogoAlt} Logo`}
              className="h-14 w-14 md:h-16 md:w-16 object-contain"
            />
            <h1 className="text-4xl md:text-5xl font-black text-dark-invirtus tracking-tighter font-heading drop-shadow-sm leading-[1.1]">
              {t('hero.apply_now')}
            </h1>
          </div>
          <p className="text-lg md:text-xl text-dark-invirtus/80 font-body max-w-2xl font-medium leading-relaxed">
            {t('hero.subtitle')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto -mt-10 relative z-20 px-4 sm:px-6 lg:px-8 flex flex-col gap-8">

        {/* Title Bar */}
        <div className="bg-white/95 backdrop-blur-xl rounded-[1.25rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.06)] border border-gray-100/80 p-6 md:p-8">
          <h2 className="text-2xl md:text-3xl font-bold text-dark-invirtus font-heading tracking-tight">
            {job.title} <span className="text-dark-invirtus/50 font-medium">@ {job.companyName || t('job_details.company_fallback')}</span>
          </h2>
        </div>

        {/* Job Details Card — matching InterviewJobDetails style */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          <div className="flex flex-col gap-8">
            {/* Header with title + download */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <h3 className="text-xl md:text-2xl font-bold text-dark-invirtus font-heading tracking-tight">
                  {t('job_details.title')}
                </h3>
                <Button
                  onClick={() => downloadJobOfferForCandidate(job)}
                  label={t('job_details.download')}
                  type="contained"
                  intent="secondary"
                  icon={<Download/>}
                  className="flex-shrink-0 text-sm !border-0 ![box-shadow:none] ![--inv-stroke:transparent]"
                  style={brandedButtonStyle}
                />
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm font-body text-dark-invirtus/70">
                {job.companyName && (
                  <div className="flex items-center gap-1.5">
                    <Briefcase className="w-4 h-4 text-dark-invirtus" />
                    <span>{job.companyName}</span>
                  </div>
                )}
                {job.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-dark-invirtus" />
                    <span>{job.location}</span>
                  </div>
                )}
                {job.contractType && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-dark-invirtus" />
                    <span>{formatEnumString(job.contractType)}</span>
                  </div>
                )}
                {job.requiredDegree && (
                  <div className="flex items-center gap-1.5">
                    <GraduationCap className="w-4 h-4 text-dark-invirtus" />
                    <span>{job.requiredDegree}</span>
                  </div>
                )}
              </div>
            </div>

            <hr className="border-gray-200/60" />

            {/* Two-Column Content */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {/* Left: Descriptions */}
              <div className="md:col-span-2 space-y-8">
                {job.description && (
                  <section>
                    <h4 className="text-sm font-bold text-dark-invirtus uppercase tracking-widest mb-3 opacity-60">{t('sections.the_role')}</h4>
                    <p className="text-dark-invirtus/80 font-body leading-relaxed">{job.description}</p>
                  </section>
                )}
                {job.mission_role_in_job && (
                  <section>
                    <h4 className="text-sm font-bold text-dark-invirtus uppercase tracking-widest mb-3 opacity-60">{t('sections.mission')}</h4>
                    <p className="text-dark-invirtus/80 font-body leading-relaxed">{job.mission_role_in_job}</p>
                  </section>
                )}
                {job.companyDescription && (
                  <section>
                    <h4 className="text-sm font-bold text-dark-invirtus uppercase tracking-widest mb-3 opacity-60">{t('sections.about_company')}</h4>
                    <p className="text-dark-invirtus/80 font-body leading-relaxed">{job.companyDescription}</p>
                  </section>
                )}
              </div>

              {/* Right: Tags */}
              <div className="md:col-span-1 space-y-6 h-fit">
                {skillStack.length > 0 ? (
                  <section>
                    <h4 className="text-sm font-bold text-dark-invirtus uppercase tracking-widest mb-2 opacity-60">{t('sections.skill_stack')}</h4>
                    {renderBadgeList(skillStack)}
                  </section>
                ) : null}
                {job.softSkills && job.softSkills.length > 0 && (
                  <section>
                    <h4 className="text-sm font-bold text-dark-invirtus uppercase tracking-widest mb-2 opacity-60">{t('sections.soft_skills')}</h4>
                    {renderBadgeList(job.softSkills)}
                  </section>
                )}
                {job.businessKnowledge && job.businessKnowledge.length > 0 && (
                  <section>
                    <h4 className="text-sm font-bold text-dark-invirtus uppercase tracking-widest mb-2 opacity-60">{t('sections.business_domain')}</h4>
                    {renderBadgeList(job.businessKnowledge)}
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Upload CV Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-2">
            <h3 className="text-xl md:text-2xl font-bold text-dark-invirtus font-heading tracking-tight">
              {t('upload.title')}
            </h3>
          </div>
          <p className="text-sm text-dark-invirtus/60 font-body leading-relaxed mb-6">
            {t('upload.description')}
          </p>

          {uploadSuccess ? (
            <div className="flex items-center gap-4 bg-green-50/50 border border-green-100 rounded-xl px-5 py-4">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <p className="text-sm text-dark-invirtus/70 font-body">
                {t('upload.success')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* File selector row */}
              <label
                htmlFor="cv-file-input"
                className={`flex items-center gap-4 rounded-xl px-5 py-4 cursor-pointer transition-colors ${
                  cvFile
                    ? 'bg-purple/5 border border-purple/20'
                    : 'bg-gray-50 border border-dashed border-gray-200 hover:border-purple/30 hover:bg-purple/5'
                }`}
              >
                {cvFile ? (
                  <>
                    <FileText className="w-6 h-6 text-purple flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-dark-invirtus font-body truncate">{cvFile.name}</p>
                      <p className="text-xs text-dark-invirtus/40 font-body">{formatFileSize(cvFile.size)}</p>
                    </div>
                    <Button
                      type="icon"
                      intent="muted"
                      size="xs"
                      onClick={(e) => { e.preventDefault(); setCvFile(null); }}
                      aria-label="Remove CV file"
                      icon={<X />}
                    />
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-dark-invirtus/25 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-dark-invirtus/60 font-body">{t('upload.select_file')}</p>
                      <p className="text-xs text-dark-invirtus/30 font-body">{t('upload.file_types')}</p>
                    </div>
                  </>
                )}
              </label>

              <input
                type="file"
                id="cv-file-input"
                className="hidden"
                accept=".pdf,.docx,.doc,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelected(file);
                  e.target.value = '';
                }}
                disabled={isUploading}
              />

              {/* Opt-in (default checked). Submitting = agreeing to be
                  contacted on WhatsApp; the wording points opt-outs to support. */}
              <label
                htmlFor="invirtus-contact-optin"
                className="mt-5 flex items-start gap-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  id="invirtus-contact-optin"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple focus:ring-purple cursor-pointer flex-shrink-0"
                  checked={hasOptedIn}
                  onChange={(e) => setHasOptedIn(e.target.checked)}
                  disabled={isUploading}
                />
                <span className="text-xs leading-relaxed text-dark-invirtus/70 font-body">
                  {t('upload.optin')}{" "}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="font-semibold text-purple hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </span>
              </label>

              {/* Mirrors the "Chat with us on WhatsApp" pill below — same
                  shape, same size, same right-aligned position, black so the
                  two CTAs sit symmetrically across the two cards. */}
              <div className="mt-6 flex justify-end">
                <Button
                  htmlType="submit"
                  type="contained"
                  intent="secondary"
                  size="md"
                  label={isUploading ? t('upload.uploading') : t('upload.submit')}
                  icon={<Upload/>}
                  loading={isUploading}
                  disabled={isSubmitDisabled}
                  className="min-w-[260px] shrink-0 !border-0 ![box-shadow:none] ![--inv-stroke:transparent]"
                  style={isSubmitDisabled ? undefined : brandedButtonStyle}
                />
              </div>
            </form>
          )}
        </div>

        {/* Need-more-info / WhatsApp CTA — dedicated card so the candidate
            has a clear way to reach the team beyond just submitting a CV. */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex flex-col items-start gap-5 p-6 md:flex-row md:items-center md:justify-between md:gap-8 md:p-8">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#25D366]/10 text-[#25D366]">
                <WhatsAppIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-dark-invirtus font-heading tracking-tight">
                  {t('whatsapp.title')}
                </h3>
                <p className="mt-1.5 max-w-md text-sm leading-relaxed text-dark-invirtus/65 font-body">
                  {t('whatsapp.description')}
                </p>
              </div>
            </div>
            <a
              href={WHATSAPP_CHAT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-[260px] shrink-0 items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1ebe5c] font-body whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#25D366]/40"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {t('whatsapp.button')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
